import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, CardContent } from '@mui/material';
import { AuthenticatedTemplate } from "@azure/msal-react";
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { ContractOverview, ContractCreateHeader, ContractTabSection } from '../components/sections';
import { useAppSelector, useAppDispatch } from '../../../app/store';
import { useGetContractQuery } from '../../../services/ContractService';
import { setIsEditing, clearSelectedContract } from '../../../services/ContractService/contractSlice';
import { selectCurrentContract, selectIsDirty } from '../../../services/ContractService/contractSlice/selectors';
import { PageLoading as Loading } from '../../common/components';

// Component for handling missing parameters
const MissingParameterError: React.FC = () => (
  <PageContainer>
    <Box display="flex" justifyContent="center">
      <PageCard>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Missing required parameters
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Both company and contract ID are required to edit a contract.
          </Typography>
        </CardContent>
      </PageCard>
    </Box>
  </PageContainer>
);

// Component for loading state
const LoadingState: React.FC = () => <Loading />;

// Component for error state
const ErrorState: React.FC = () => (
  <PageContainer>
    <Box display="flex" justifyContent="center">
      <PageCard>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Error loading contract
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please try again or go back to the contract list.
          </Typography>
        </CardContent>
      </PageCard>
    </Box>
  </PageContainer>
);

// Component for contract not found
const ContractNotFound: React.FC = () => (
  <PageContainer>
    <Box display="flex" justifyContent="center">
      <PageCard>
        <CardContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Contract not found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The requested contract could not be found.
          </Typography>
        </CardContent>
      </PageCard>
    </Box>
  </PageContainer>
);

const ContractEdit: React.FC = () => {
  // Hot reload test - testing RTK Query refetch functionality
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const company = searchParams.get('company');
  const contractId = searchParams.get('id');
  
  const currentContract = useAppSelector(selectCurrentContract);
  const isDirty = useAppSelector(selectIsDirty);

  // Query to fetch the contract
  const { error, isLoading, refetch } = useGetContractQuery(
    { id: contractId!, company: company! },
    { 
      skip: !contractId || !company,
      refetchOnMountOrArgChange: true  // Always refetch when component mounts
    }
  );



  useEffect(() => {
    // Set editing mode when contract is loaded
    if (currentContract) {
      dispatch(setIsEditing(true));
    }
  }, [currentContract, dispatch]);

  useEffect(() => {
    // Redirect if required params are missing
    if (!company || !contractId) {
      navigate('/contracts');
      return;
    }
  }, [company, contractId, navigate]);

  // Cleanup on unmount - don't clear contract, let RTK Query cache manage it
  useEffect(() => {
    return () => {
      // Component cleanup - no longer clearing selected contract
      // RTK Query will refetch when component remounts
    };
  }, []);

  // Handle missing parameters
  if (!company || !contractId) {
    return (
      <AuthenticatedTemplate>
        <MissingParameterError />
      </AuthenticatedTemplate>
    );
  }

  // Handle loading state
  if (isLoading) {
    return (
      <AuthenticatedTemplate>
        <LoadingState />
      </AuthenticatedTemplate>
    );
  }

  // Handle error state
  if (error) {
    return (
      <AuthenticatedTemplate>
        <ErrorState />
      </AuthenticatedTemplate>
    );
  }

  // Handle contract not found
  if (!currentContract) {
    return (
      <AuthenticatedTemplate>
        <ContractNotFound />
      </AuthenticatedTemplate>
    );
  }

  return (
    <AuthenticatedTemplate>
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard>
            <ContractCreateHeader 
              company={company} 
              isDirty={isDirty} 
              isEditMode={true}
              contractName={currentContract ? `${currentContract.payer} - ${currentContract.product}` : undefined}
            />
            
            <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
              <Stack spacing={3}>
                <ContractOverview />
                <ContractTabSection />
              </Stack>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    </AuthenticatedTemplate>
  );
};

export default ContractEdit;
